import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'

import {
  PAYMENT_BALANCE_MODE,
  balanceMethodOptions,
  balanceTermBeginsOptions,
  balanceTimeOptions,
  paymentMethodOptions,
  paymentTermsOptions,
  PaymentSettlementBanner,
  useDefaultMethodsAutofill,
} from '#/entities/shipment'
import type { PaymentFormulaResult } from '#/entities/shipment'
import { formatUsd } from '#/shared/lib/format'
import { MANAGE_FIELD_KEY, useIsFieldHidden } from '#/shared/lib/manage-fields'
import { FormCard } from '#/shared/ui/form-card'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import { PriceCheckBadge } from '#/shared/ui/price-check-badge'
import { Select } from '#/shared/ui/select'
import { cn } from '#/shared/lib/utils'
import type { PaymentFormData } from '../model/payment-schema'

interface SelectValues {
  paymentMethod: string
  paymentTerms: string
  balanceMethod: string
  balanceTime: string
  balanceTermBegins: string
}

interface PaymentCardProps {
  control: Control<PaymentFormData>
  register: UseFormRegister<PaymentFormData>
  getValues: UseFormGetValues<PaymentFormData>
  setValue: UseFormSetValue<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  formula: PaymentFormulaResult
  selectValues: SelectValues
  onPriceCheck?: () => void
  onCarrierFeeChange?: (value: string) => void
  /**
   * Optional "Check rate" control (Super Dispatch market rate). Injected by the
   * widget layer — `order-create` (a feature) can't import `market-rate` — so it
   * arrives as a ready-rendered node and self-hides without a shipment id.
   */
  rateCheck?: ReactNode
}

const FIELD_PROPS = {
  inputSize: 'sm' as const,
  labelSize: 'sm' as const,
  clearable: true,
}

const MONEY_PROPS = {
  ...FIELD_PROPS,
  mask: '$',
  numberFormat: true,
  placeholder: '0.00',
}

type MoneyFieldName = 'totalTariff' | 'deposit' | 'carrierFee'

function MoneyField({
  name,
  label,
  control,
  errors,
  width,
  onValueChange,
  placeholder = MONEY_PROPS.placeholder,
}: {
  name: MoneyFieldName
  label: string
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  width: string
  onValueChange?: (value: string) => void
  placeholder?: string
}) {
  return (
    <div className={cn('min-w-0', width)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            {...MONEY_PROPS}
            placeholder={placeholder}
            label={label}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value)
              onValueChange?.(e.target.value)
            }}
            onBlur={field.onBlur}
            name={field.name}
            error={!!errors[name]}
          />
        )}
      />
    </div>
  )
}

function ReadOnlyMoneyField({
  label,
  amount,
  width,
}: {
  label: string
  amount: number
  width: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5', width)}>
      <Label size="sm">{label}</Label>
      <div className="flex h-8 items-center overflow-hidden rounded-sm border border-gray-200 bg-gray-100 px-2 text-sm whitespace-nowrap text-gray-900">
        {formatUsd(amount, { showCents: true })}
      </div>
    </div>
  )
}

type SelectFieldName =
  | 'paymentMethod'
  | 'paymentTerms'
  | 'balanceMethod'
  | 'balanceTime'
  | 'balanceTermBegins'

function SelectField({
  name,
  label,
  control,
  options,
  width,
  required,
}: {
  name: SelectFieldName
  label: string
  control: Control<PaymentFormData>
  options: ReturnType<typeof paymentMethodOptions>
  width: string
  required?: boolean
}) {
  const labelText = required ? `${label} *` : label
  return (
    <div className={cn('min-w-0', width)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            label={labelText}
            labelSize="sm"
            selectSize="sm"
            options={options}
            value={field.value || undefined}
            onValueChange={field.onChange}
          />
        )}
      />
    </div>
  )
}

function MoneyRow({
  control,
  errors,
  formula,
  onCarrierFeeChange,
}: {
  control: Control<PaymentFormData>
  errors: FieldErrors<PaymentFormData>
  formula: PaymentFormulaResult
  onCarrierFeeChange?: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-2.5">
      <MoneyField
        name="totalTariff"
        label={`${t('orderCreate.payment.totalTariff')} *`}
        control={control}
        errors={errors}
        width="flex-1"
      />
      <MoneyField
        name="deposit"
        label={`${t('orderCreate.payment.deposit')} *`}
        control={control}
        errors={errors}
        width="flex-1"
      />
      <MoneyField
        name="carrierFee"
        label={`${t('orderCreate.payment.carrierFee')} *`}
        control={control}
        errors={errors}
        width="flex-1"
        onValueChange={onCarrierFeeChange}
        placeholder=""
      />
      <ReadOnlyMoneyField
        label={t('orderCreate.payment.dueOnDelivery')}
        amount={formula.dueOnDelivery}
        width="flex-1"
      />
    </div>
  )
}

function CodSelectsRow({ control }: { control: Control<PaymentFormData> }) {
  const { t } = useTranslation()
  return (
    <div className="flex animate-slide-down items-start gap-2.5">
      <SelectField
        name="paymentMethod"
        label={t('orderCreate.payment.paymentMethod')}
        control={control}
        options={paymentMethodOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="paymentTerms"
        label={t('orderCreate.payment.terms')}
        control={control}
        options={paymentTermsOptions(t)}
        width="flex-1"
        required
      />
    </div>
  )
}

function BalanceRow({
  control,
  formula,
  showCodSelects,
}: {
  control: Control<PaymentFormData>
  formula: PaymentFormulaResult
  showCodSelects: boolean
}) {
  const { t } = useTranslation()
  if (showCodSelects) {
    return (
      <div className="flex animate-slide-down items-start gap-2.5">
        <ReadOnlyMoneyField
          label={t('orderCreate.payment.balance')}
          amount={formula.balanceAbsolute}
          width="flex-1"
        />
        <SelectField
          name="paymentMethod"
          label={t('orderCreate.payment.paymentMethod')}
          control={control}
          options={paymentMethodOptions(t)}
          width="flex-1"
          required
        />
        <SelectField
          name="paymentTerms"
          label={t('orderCreate.payment.terms')}
          control={control}
          options={paymentTermsOptions(t)}
          width="flex-1"
          required
        />
      </div>
    )
  }
  return (
    <div className="flex animate-slide-down items-start gap-2.5">
      <ReadOnlyMoneyField
        label={t('orderCreate.payment.balance')}
        amount={formula.balanceAbsolute}
        width="w-22.5 shrink-0"
      />
      <SelectField
        name="balanceMethod"
        label={t('orderCreate.payment.balanceMethod')}
        control={control}
        options={balanceMethodOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTime"
        label={t('orderCreate.payment.balanceTime')}
        control={control}
        options={balanceTimeOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTermBegins"
        label={t('orderCreate.payment.balanceTermBegins')}
        control={control}
        options={balanceTermBeginsOptions(t)}
        width="flex-1"
        required
      />
    </div>
  )
}

function SettlementSelectsRow({
  control,
}: {
  control: Control<PaymentFormData>
}) {
  const { t } = useTranslation()
  return (
    <div className="flex animate-slide-down items-start gap-2.5">
      <SelectField
        name="balanceMethod"
        label={t('orderCreate.payment.balanceMethod')}
        control={control}
        options={balanceMethodOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTime"
        label={t('orderCreate.payment.balanceTime')}
        control={control}
        options={balanceTimeOptions(t)}
        width="flex-1"
        required
      />
      <SelectField
        name="balanceTermBegins"
        label={t('orderCreate.payment.balanceTermBegins')}
        control={control}
        options={balanceTermBeginsOptions(t)}
        width="flex-1"
        required
      />
    </div>
  )
}

export function PaymentCard({
  control,
  register,
  getValues,
  setValue,
  errors,
  formula,
  selectValues,
  onPriceCheck,
  onCarrierFeeChange,
  rateCheck,
}: PaymentCardProps) {
  const { t } = useTranslation()
  const showDefaultMethods = !useIsFieldHidden(
    MANAGE_FIELD_KEY.paymentDefaultMethods,
  )
  useDefaultMethodsAutofill(showDefaultMethods, getValues, setValue)
  const showCodSelects =
    formula.mode === PAYMENT_BALANCE_MODE.COD_ONLY ||
    formula.mode === PAYMENT_BALANCE_MODE.CARRIER_OWES_US
  const showBalanceRow = formula.hasBalance
  const showSettlementSelects =
    formula.mode === PAYMENT_BALANCE_MODE.CARRIER_OWES_US

  return (
    <FormCard
      title={t('orderCreate.payment.title')}
      className="pb-5"
      contentClassName="gap-3"
      actions={
        <div className="flex items-center gap-3">
          {rateCheck}
          <PriceCheckBadge
            label={t('orderCreate.payment.priceCheck')}
            onClick={onPriceCheck}
          />
        </div>
      }
    >
      <MoneyRow
        control={control}
        errors={errors}
        formula={formula}
        onCarrierFeeChange={onCarrierFeeChange}
      />
      {showDefaultMethods && showBalanceRow && (
        <BalanceRow
          control={control}
          formula={formula}
          showCodSelects={showCodSelects}
        />
      )}
      {showDefaultMethods && !showBalanceRow && showCodSelects && (
        <CodSelectsRow control={control} />
      )}
      {showDefaultMethods && showSettlementSelects && (
        <SettlementSelectsRow control={control} />
      )}
      {formula.mode !== PAYMENT_BALANCE_MODE.NONE && (
        <PaymentSettlementBanner formula={formula} values={selectValues} />
      )}
      <Input
        {...FIELD_PROPS}
        {...register('paymentNote')}
        label={t('orderCreate.payment.paymentNote')}
        error={!!errors.paymentNote}
      />
    </FormCard>
  )
}
